import { useGetAllMilestonesQuery } from 'api/siteApi';
import { RotateCcw } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { Skeleton } from 'ui';
import { isInIframe } from 'utils/proxyUtils';
import { useResponsive } from 'utils/useMediaQuery';
import Button from '../../../../../packages/ui/components/Button';
import { enterpriseClients } from '../../hooks/use-enterprise-account';
import { usePermissions } from '../../hooks/use-permissions';
import { getSelectedSiteCollaborateInfo } from '../../store/siteInfoSlice';
import { getBrowseMode, getIsBottomBarLoading } from '../../store/uiSlice';
import { CollaboratorSubmit } from './collaborator-submit-button';
import { DeviceMenu } from './device-menu/device-menu';
import { MiniMenu } from './mini-menu/mini-menu';
import { PageStatus } from './page-status/page-status';
import { ProjectStages } from './project-stages/project-stages';

interface Props {
  siteId?: string | null;
}

export const PageControls = ({ siteId }: Props) => {
  const { isPermitted } = usePermissions();
  const browseMode = useSelector(getBrowseMode);
  const itemDetails = useSelector(getSelectedSiteCollaborateInfo)
  const { t } = useTranslation();
  const { data } = useGetAllMilestonesQuery({ siteId: siteId ?? '' }, { skip: !siteId })
  const { isMobile } = useResponsive();
  const isBottomBarLoading = useSelector(getIsBottomBarLoading);


  useEffect(() => {
    if (!isInIframe()) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'REFRESH_RATE_LIMITED') {

        // Show toast with refresh cooldown information
        toast.error(`Rate limited. Please wait ${Math.ceil(event.data.nextRefreshAllowedIn / 1000)} seconds before refreshing again`);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [t]);

  const handleRefresh = () => {
    if (!isInIframe()) return;

    window.parent.postMessage(
      {
        type: 'REFRESH_IFRAME',
      },
      '*'
    );
  };


  // const hasPermission = useMemo(() => {
  //   return itemDetails?.result?.role === 'admin' || itemDetails?.result?.role === 'contributor';
  // }, [itemDetails?.result?.role]);

  const isGodaddyGuest = itemDetails?.result?.godaddy_username || (itemDetails?.result?.account_id !== undefined && enterpriseClients.includes(itemDetails?.result?.account_id));

  return (
    <div className="flex gap-1.5 items-center">
      <MiniMenu />
      {isInIframe() && (
        <>
          {isBottomBarLoading ? (
            <>
              <Skeleton className="h-9 w-9 rounded-md" />
            </>
          ) : (
            <>
              <Button
                onClick={handleRefresh}
                size="icon"
                variant="outline"
                tooltip={t("bottom_bar.refresh_tooltip", "Refresh the current page to sync changes")
                }
                tooltipSide="top"
              >
                <RotateCcw size={18} />
              </Button>
            </>
          )}
        </>
      )}
      <AnimatePresence>
        {!browseMode && !isGodaddyGuest && (
          <>
            {/* FIX ME -  PAGESTATUS BUTTON SHOULD ONLY SHOW FOR COLLABORATORS */}
            <motion.div
              initial={{ opacity: 0, x: -50, width: 0, marginRight: '-6px' }}
              animate={{ opacity: 1, x: 0, width: 'auto', marginRight: '0px' }}
              exit={{
                opacity: 0, x: -50, width: 0,
                transition: { delay: 0, duration: 0.2 }
              }}
              transition={{ duration: 0.4, delay: 0.75, type: 'spring', stiffness: 300, damping: 16, mass: 0.5 }}>
              <PageStatus siteId={siteId} />
            </motion.div>
          </>
        )
        }
      </AnimatePresence>
      {
        !browseMode && isGodaddyGuest && (
          <CollaboratorSubmit />
        )
      }
      {
        !isMobile && isPermitted("collaborate-bottom-bar-responsive-modes") && (
          <DeviceMenu />
        )}
      {
        (!browseMode && !!data?.result?.length) &&
        <motion.div initial={{ opacity: 0, x: -50, width: 0 }}
          animate={{ opacity: 1, x: 0, width: 'auto' }}
          exit={{ opacity: 0, x: -50, width: 0 }}
          transition={{ duration: 0.4, delay: 0.75, type: 'spring', stiffness: 300, damping: 16, mass: 0.5 }}>
          <ProjectStages siteId={siteId} />
        </motion.div>
      }
    </div >
  );
};
